import { Change } from '@adornis/base/change.js';
import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import type { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import { selectionSet, type BaseQLSelectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { DateTime } from 'luxon';
import { switchMap } from 'rxjs';
import { Appointment } from '../entities/appointment.js';
import { BHFilter } from '../entities/bh-filter.js';
import { Category } from '../entities/category.js';
import { Einzelberatung } from '../entities/einzelberatung.js';
import { Institution } from '../entities/institution.js';
import { Participation } from '../entities/Participation.js';
import { ProductEntryRequirement } from '../entities/ProductEntryRequirement.js';
import {
  Product,
  type VersionChangesAttrMap,
  type VersionChangesList,
  type VersionChangeType,
} from '../entities/products.js';
import {
  ProductUpdateWithReferences,
  type ProductUpdateWithReferencesNarrEnt,
} from '../entities/ProductUpdateWithReferences.js';
import { Setting } from '../entities/settings.js';
import { Teaching } from '../entities/teaching.js';
import { TextMultipleLangItem } from '../entities/TextMultipleLangItem.js';
import { User } from '../entities/user.js';
import { Workshop, WorkshopAffiliation } from '../entities/workshop.js';
import { WorkshopNote } from '../entities/WorkshopNote.js';
import { ParticipationState } from '../enums/enums.js';
import { Permission } from '../enums/roles.js';
import { notifyProductCanceled } from '../notification/product-cancled.js';
import { notifyProductDecanceled } from '../notification/product-decancled.js';
import { notifyProductChanged } from '../notification/workshop-changed.js';
import { participationRetrievesProductUpdateMail } from '../server/logic/participation.js';
import { lookupEntityById, searchMatch } from '../util.js';
import { getProductTeachings } from './teaching.js';

export const setProductCancleStatus = registerMutation({
  operationName: 'setProductCancleStatus',
  type: () => Int,
  params: [
    { name: 'workshopID', type: () => String },
    { name: 'cancled', type: () => Boolean },
  ],
  resolve: (productID: string, cancled: boolean) => {
    return async () => {
      if (context.serverContext) return;
      const contextUserID = context.userID as string;
      if (!contextUserID) throw new Error('user not logged in');
      const currentUser = await User.getByID<User>(contextUserID)({ roles: AdornisRoleToContextsWrapper.allFields });
      if (!currentUser) throw new Error('user not logged in');
      const product = await getByID(Product, productID)({ institutionID: 1, isCanceled: 1 });
      if (!product) throw new Error('workshop not found');
      const hasPermission = (() => {
        if (product instanceof Workshop)
          return currentUser.hasPermission(Permission.EDIT_ADMIN_INSTITUTION_WORKSHOPS, product.institutionID);
        if (product instanceof Einzelberatung)
          return currentUser.hasPermission(Permission.EDIT_ADMIN_INSTITUTION_COUNSELINGS, product.institutionID);
        throw new Error('unreachable');
      })();

      if (!hasPermission) throw new Error('no permission to do that');

      if (product.isCanceled === cancled) {
        logger.warn(
          { productID },
          'product.isCancled is already = ' + cancled + '; calling this function is propably a mistake',
        );
        return;
      }

      product.isCanceled = cancled;
      await product.save();

      // send mail to participants
      const participantions = await Product.getParticipantsForProduct(productID);
      for (const { user, participation } of participantions) {
        const notifyStates = [ParticipationState.LEFT, ParticipationState.WAITING];
        if (notifyStates.includes(participation.state)) continue;

        try {
          if (cancled) await notifyProductCanceled(productID, user._id);
          else await notifyProductDecanceled(productID, user._id);
        } catch (err) {
          logger.error({ err, productID, userID: user._id }, 'could not send mail to participant');
        }
      }
    };
  },
});

export const copyProductsIntoNewTimeframe = registerMutation({
  operationName: 'copyWorkshopsIntoNewTimeframe',
  type: () => Int,
  params: [
    { name: 'workshopIDs', type: () => [String] },
    { name: 'timeframeID', type: () => String },
  ],
  resolve: (productIDs: string[], timeframeID: string) => {
    return async () => {
      const productCollection = await getRawCollection(Product._collectionName);
      const productEntryRequirementCollection = await getRawCollection(ProductEntryRequirement._collectionName);

      for (const productID of productIDs) {
        const productData = await productCollection.findOne<EntityData<Product>>({ _id: productID as any });
        const settingsData = await loadMongoData(Setting, { productID: productID }).then(d => d[0]);
        const teachingsData = await loadMongoData(Teaching, { productID: productID });

        const productEntryRequirementsData = await productEntryRequirementCollection
          .find({ productID: productID })
          .toArray();

        if (!productData) throw new Error('product not found');

        // copy product
        const copiedProduct = {
          ...productData,
          _id: A.getGloballyUniqueID(),
          productCopyID: productData._id,
          timeframeID,
        };

        // create new appointmentIDs
        for (const appointment of copiedProduct.appointments ?? []) {
          appointment._id = A.getGloballyUniqueID();
        }

        // copy settings
        const copiedSettings = new Setting({
          ...settingsData,
          _id: A.getGloballyUniqueID(),
          timeframeID,
          productID: copiedProduct._id,
        });

        // copy lecturers
        const copiedTeachings = teachingsData.map(
          t => new Teaching({ productID: copiedProduct._id, lecturerID: t.lecturerID }),
        );

        // copy entry requirements
        const copiedProductEntryRequirements = productEntryRequirementsData.map(data => ({
          ...data,
          _id: A.getGloballyUniqueID(),
          productID: copiedProduct._id,
        }));

        if (copiedProduct._class === Workshop._class) {
          const copiedWorkshop = copiedProduct as EntityData<Workshop>;

          // reset appointment dates
          copiedWorkshop.appointments.forEach(ap => {
            ap.start = null;
            ap.end = null;
          });
        }

        if (copiedProduct._class === Einzelberatung._class) {
          const copiedEinzelberatung = copiedProduct as EntityData<Einzelberatung>;

          copiedEinzelberatung.slotConfig = null;
        }

        await productCollection.insertOne(copiedProduct as any);
        await copiedSettings.create();
        for (const teaching of copiedTeachings) await teaching.create();
        if (productEntryRequirementsData.length > 0) {
          await productEntryRequirementCollection.insertMany(copiedProductEntryRequirements as any);
        }
      }
      return 0;
    };
  },
});

export const subscribeAllProductsByInstitution = registerSubscription({
  operationName: 'subscribeAllProductsByInstitution',
  type: () => [Product],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  resolve: (skip: number = 0, limit: number = 99999, filter?: BHFilter) => {
    return (gqlFields: BaseQLSelectionSet<Product>) => {
      const institutionID = filter?.institutionID;
      if (!institutionID) throw new Error('Missing institutionID');

      const aggregation = [
        { $match: { institutionID } },
        { $match: searchMatch(['name', 'description'], filter.search) },
        { $lookup: lookupEntityById(Category, 'categoryID', 'category') },
        { $set: { category: { $arrayElemAt: ['$category', 0] } } },
        { $lookup: lookupEntityById(Timeframe, 'timeframeID', 'timeframe') },
        { $set: { timeframe: { $arrayElemAt: ['$timeframe', 0] } } },
        { $match: filter.getMongoFilter() ?? {} },
        { $sort: filter?.getMongoOptions().sort ?? {} },
        { $skip: skip },
        { $limit: limit },
      ];

      return watchEntireCollection(Product._collectionName).pipe(
        switchMap(async () => {
          const productCollection = await getRawCollection<Product>(Product._collectionName);
          return productCollection.aggregate(aggregation, { collation: { locale: 'de' } }).toArray();
        }),
      );
    };
  },
});

export const subscribeMaterialiedProductsById = registerSubscription({
  operationName: 'subscribeMaterialiedProductsById',
  type: () => [Product],
  params: [{ name: 'productIDs', type: () => [String] }],
  resolve: (productIDs: string[]) => {
    return <SelSet extends BaseQLSelectionSet<Product | Workshop | Einzelberatung>>(gqlFields: SelSet) => {
      return watchEntireCollection('products-materialized').pipe(
        switchMap(async () => {
          const rawCollection = await getRawCollection<Product>('products-materialized');
          const result = await rawCollection
            .aggregate<Product>([{ $match: { _id: { $in: productIDs } } }, { $project: gqlFields }])
            .toArray();
          return result;
        }),
      );
    };
  },
});

const emptySelSet = {
  _id: 1,
  _class: 1,
} as const;
export const versionDiffSettingSelSet = {
  ...emptySelSet,

  // Use for checks and mail-content
  courseLocation: 1,
  inheritedCourseLocation: 1,
  inheritedCapacity: 1,
  infoLink: 1,
  inheritedInfoLink: 1,
  extraParticipationConstraints: 1,
  parentInstitutionID: 1,
  institutionID: 1,

  // use for formular
  paymentMethods: 1,
  productID: 1,
  secondReminderLeadTime: 1,
  blockSignOffDaysInAdvance: 1,
  blockAutomaticMoveUpDaysInAdvance: 1,
  firstReminderLeadTime: 1,
  firstAppointmentReminderLeadTime: 1,
  secondAppointmentReminderLeadTime: 1,
  meetingInfoMailLeadTime: 1,
  qrCodeValidityInMinutes: 1,
  autoNachrueckenEnabled: 1,
  autoSendCertifatesEnabled: 1,

  inheritedBlockSignOffDaysInAdvance: 1,
  inheritedSecondReminderLeadTime: 1,
  inheritedBlockAutomaticMoveUpDaysInAdvance: 1,
  inheritedFirstReminderLeadTime: 1,
  inheritedFirstAppointmentReminderLeadTime: 1,
  inheritedSecondAppointmentReminderLeadTime: 1,
  inheritedMeetingInfoMailLeadTime: 1,
  inheritedQrCodeValidityInMinutes: 1,
} as const;
export const versionDiffAppointmentSelSet = {
  ...emptySelSet,
  location: 1,
  name: 1,
  start: 1,
  end: 1,
  isNonBlocking: 1,
  lastReminderMailSent: 1,
} as const;
export const versionDiffProductSelSet = {
  ...emptySelSet,

  reference: 1,
  description: 1,
  sellerID: 1,

  infoLink: 1,
  name: 1,
  capacity: 1,
  presenceType: 1,
  hasPrice: 1,
  publishDate: 1,
  topic: 1,
  language: 1,
  institutionID: 1,
  timeframeID: 1,
  categoryID: 1,
  isActive: 1,
  lastReminderMailSent: 1,
  productCopyID: 1,
  isLDAPOnly: 1,
  isCanceled: 1,
  overrideInstitutionWaitinglistPriorityRules: 1,

  settings: versionDiffSettingSelSet,
  institution: selectionSet(() => Institution, 1),
  timeframe: selectionSet(() => Timeframe, 1),
  prices: { unitAmount: 1, currency: 1 },
  category: { ...emptySelSet, name: 1 },
  translatedDescriptionHTML: { langKey: 1, text: 1 },
  attachedCertificate: { certificateID: 1, descriptionsHTML: TextMultipleLangItem.allFields, exportDate: 1 },
} as const;
export const versionDiffTeachingUserSelSet = { title: 1, firstName: 1, lastName: 1 } as const;
export const versionDiffTeachingSelSet = {
  ...emptySelSet,
  lecturerID: 1,
  lecturer: versionDiffTeachingUserSelSet,
} as const;
export const versionDiffWorkshopSelSet = {
  ...versionDiffProductSelSet,
  appointments: versionDiffAppointmentSelSet,
  settings: versionDiffSettingSelSet,
  sortingWeight: 1,
  hideCapacity: 1,
  leftCapacity: 1,
  nachrueckBlockTimeForLowPriorityInDays: 1,
  detailPage: 1,

  affiliation: selectionSet(() => WorkshopAffiliation, 2),
  notes: selectionSet(() => WorkshopNote, 2),
} as const;
export type VersionDiffAppointment = NarrowedEntity<Appointment, typeof versionDiffAppointmentSelSet>;
export type VersionDiffProduct = NarrowedEntity<Product, typeof versionDiffProductSelSet>;
export type VersionDiffWorkshop = NarrowedEntity<Workshop, typeof versionDiffWorkshopSelSet>;
export type VersionDiffTeachings = NarrowedEntity<Teaching, typeof versionDiffTeachingSelSet>;
export type VersionDiffSetting = NarrowedEntity<Setting, typeof versionDiffSettingSelSet>;

type EntityFieldPath = string | number /* | undefined */;
const noDiffKeys: EntityFieldPath[] = [...Object.keys(emptySelSet) /* , undefined */];
export const noDiffInheritedKeys = (key: string) => !key.startsWith('inherited');
export const getDiffKeysOfProduct = (): EntityFieldPath[] =>
  Object.keys(versionDiffProductSelSet).filter(
    k =>
      ![
        ...noDiffKeys,
        'institutionID',
        'prices',
        'presenceType',
        'hasPrice',
        'category',
        'translatedDescriptionHTML',
        'publishDate',
        'timeframeID',
        'attachedCertificate',
        'sortingWeight',
        'hideCapacity',
        'reference',
        'sellerID',
        'categoryID',
        'isActive',
        'lastReminderMailSent',
        'productCopyID',
        'isLDAPOnly',
        'isCanceled',
        'overrideInstitutionWaitinglistPriorityRules',
        'institution',
      ].includes(k) && noDiffInheritedKeys(k),
  );
export const getDiffKeysOfSetting = (): EntityFieldPath[] =>
  Object.keys(versionDiffSettingSelSet).filter(
    k =>
      ![
        ...noDiffKeys,
        'productID',
        'secondReminderLeadTime',
        'inheritedSecondReminderLeadTime',
        'paymentMethods',
        'blockSignOffDaysInAdvance',
        'blockAutomaticMoveUpDaysInAdvance',
        'firstReminderLeadTime',
        'firstAppointmentReminderLeadTime',
        'secondAppointmentReminderLeadTime',
        'qrCodeValidityInMinutes',
        'autoNachrueckenEnabled',
        'autoMoveNoShowEnabled',
        'autoSendCertifatesEnabled',
        'leftCapacity',
        'nachrueckBlockTimeForLowPriorityInDays',
        'detailPage',
        'affiliation',
        'notes',
        'institutionID',
      ].includes(k) && noDiffInheritedKeys(k),
  );

function generateAppointmentDiff(
  oldAppointments: Array<VersionDiffAppointment>,
  newAppointments: Array<VersionDiffAppointment>,
): VersionChangesList {
  const changes: VersionChangesList = [];

  for (const curOldAppointEntity of oldAppointments) {
    const curOldAppointObj = curOldAppointEntity.toJSON();
    const appointmentNew = newAppointments.find(newApp => newApp._id === curOldAppointObj._id);
    // check for removed appointments
    if (!appointmentNew) {
      changes.push({ old: curOldAppointObj, new: {} });
    } else {
      // check for changes appointments
      const change = { old: {}, new: {} };
      for (const key of Object.keys(curOldAppointObj)) {
        let valueOld = curOldAppointObj[key];
        let valueNew = appointmentNew[key];
        if (['start', 'end'].includes(key)) {
          if (valueOld instanceof Date) valueOld = DateTime.fromJSDate(valueOld);
          if (
            valueOld instanceof DateTime &&
            valueNew instanceof DateTime &&
            (valueOld as DateTime).diff(valueNew, 'milliseconds').milliseconds !== 0
          ) {
            change.old[key] = valueOld;
            change.new[key] = valueNew;
          }
        } else if (valueOld !== valueNew) {
          change.old[key] = valueOld;
          change.new[key] = valueNew;
        }
      }

      if (!!Object.keys(change.old).length || !!Object.keys(change.new).length) {
        change.old['_id'] = curOldAppointObj._id;
        change.new['_id'] = appointmentNew._id;
        changes.push(change);
      }
    }
  }

  for (const appointmentNew of newAppointments) {
    // check for newly added appointments
    const appointmentOld = oldAppointments.find(oldApp => oldApp._id === appointmentNew._id);
    if (!appointmentOld) {
      changes.push({ old: {}, new: appointmentNew });
    }
  }

  return changes;
}

const getValueFromByPath = (entity: any, inheritedValues: Record<string, any>, fieldsPath: EntityFieldPath[]) => {
  if (fieldsPath.at(-1)?.toString().startsWith('inherited')) return '';

  const simpleResolvedValue = Change.resolvePath(entity, fieldsPath);
  let result = '';

  if (![undefined, null].includes(simpleResolvedValue))
    try {
      result = `${simpleResolvedValue}`;
    } catch (e) {}

  if (!simpleResolvedValue && `${fieldsPath.at(-1)}` in inheritedValues)
    result = `${inheritedValues[`${fieldsPath.at(-1)}`] ?? ''}`;

  return result;
};

const getInheritedProps = async (entity: MongoEntity) =>
  Object.fromEntries(
    (
      await Promise.all(
        Object.entries(
          ((entity as any as { doc: any }).doc ?? entity) as any as Record<string, Maybe<[any, string | null]>>,
        )
          .filter(([attr]) => attr.startsWith('inherited'))
          .map(async ([attr, attrValues]) => {
            if (!attrValues || !Array.isArray(attrValues) || attrValues.length < 2) attrValues = [null, null];

            let [, fieldValue] = attrValues;
            fieldValue ??= null;

            // Try to solve without DB-Request
            let normalAttrName = attr.replace('inherited', '');
            normalAttrName = normalAttrName.charAt(0).toLowerCase() + normalAttrName.slice(1);

            return [
              normalAttrName,
              normalAttrName in entity ? fieldValue : null,
              // solve with DB-Request
              // ToDo: wenn nicht benötigt dann löschen
              /* ?? entity instanceof Setting
                ? await getInheritedSetting(entity as Setting, normalAttrName as any)
                : null, */
            ];
          }),
      )
    ).filter(kv => kv != undefined),
  );

type DiffParams = [PartialEntityData<Product>, PartialEntityData<Product>];
const isVersionFieldRelevant = (attrKey: EntityFieldPath, relFields: EntityFieldPath[], value: any) =>
  relFields.includes(attrKey) && value !== undefined;

const trimmVersionEntityData = (entity: NarrowedEntity<MongoEntity>, relFields: EntityFieldPath[]) => {
  return Object.fromEntries(
    Object.entries(entity.toJSON()).filter(([key, value]) => isVersionFieldRelevant(key, relFields, value)),
  ) as PartialEntityData<Product>;
};
export const generateProductVersionDiff = async (
  {
    product: oldProd,
    teachings: oldTeachs,
    appointments: oldAppointments,
    setting: oldConf,
  }: ProductUpdateWithReferencesNarrEnt,
  {
    product: newProd,
    teachings: newTeachs,
    appointments: newAppointments,
    setting: newConf,
  }: ProductUpdateWithReferencesNarrEnt,
): Promise<VersionChangesAttrMap> => {
  const prodDiffParams = [oldProd, newProd].map(e =>
    trimmVersionEntityData(e as any, getDiffKeysOfProduct()),
  ) as DiffParams;
  const confDiffParams = (
    !oldConf && !newConf
      ? [{}, {}]
      : [oldConf ?? new Setting({}), newConf ?? new Setting({})].map(e =>
          trimmVersionEntityData(e, getDiffKeysOfSetting()),
        )
  ) as DiffParams;

  const oldTeachings = oldTeachs ?? [];
  const newTeachings = newTeachs ?? [];

  const isRelevant = (relFields: (EntityFieldPath | undefined)[], change: Change) =>
    relFields.includes(change.getPath().at(-1)) && change.getValue() !== undefined;

  // mach KeyValuePairs draus und nimm als value default den wert aus inherited
  const prodInheritedProps = oldProd ? await getInheritedProps(oldProd as any) : {};
  const confInheritedProps = oldConf ? await getInheritedProps(oldConf as any) : {};

  logger.info(
    { confDiffParams, o: oldConf?.inheritedInfoLink /* n: newConf?.inheritedInfoLink */ },
    '>>>>>>>>>>> generateProductVersionDiff',
  );
  console.log('>>>>>>>>>>> generateProductVersionDiff', { confDiffParams });

  const prodDiff =
    Change.getDiff(...prodDiffParams)
      ?.filter(c => isRelevant(getDiffKeysOfProduct(), c))
      .map<[string | number | undefined, VersionChangeType]>(change => [
        change.getPath().at(-1),
        {
          new: getValueFromByPath(newProd, prodInheritedProps, change.getPath()),
          old: getValueFromByPath(oldProd, prodInheritedProps, change.getPath()),
        },
      ]) ?? [];

  const confDiff = (Change.getDiff(...confDiffParams)?.filter(c => isRelevant(getDiffKeysOfSetting(), c)) ?? []).map<
    [string | number | undefined, VersionChangeType]
  >(change => [
    change.getPath().at(-1),
    {
      new: getValueFromByPath(newConf, confInheritedProps, change.getPath()),
      old: getValueFromByPath(oldConf, confInheritedProps, change.getPath()),
    },
  ]);

  const mabyeAppointmentsChanges: ['appointments', VersionChangeType[]][] = [];
  if (oldProd._class === 'Workshop' && oldAppointments && newAppointments) {
    const appChanges = generateAppointmentDiff(
      oldAppointments,
      (newAppointments as any as { doc: any }[]).map(a => a.doc),
    );

    if (appChanges.length) mabyeAppointmentsChanges.push(['appointments', appChanges]);
  }

  const changes = {
    ...Object.fromEntries([...prodDiff, ...confDiff, ...mabyeAppointmentsChanges]),
  } as VersionChangesAttrMap;

  const findTeachingUser = (teachings: VersionDiffTeachings[], lecturerID: string) =>
    teachings.find(t => t.lecturerID === lecturerID);

  if (oldTeachings.length + newTeachings.length > 0) {
    const remTeachingChanges = oldTeachings.filter(t => !findTeachingUser(newTeachings, t.lecturerID));
    const addTeachingChanges = newTeachings.filter(nt => !findTeachingUser(oldTeachings, nt.lecturerID));

    if (remTeachingChanges.length + addTeachingChanges.length > 0)
      changes['teachings'] = {
        old: remTeachingChanges.map(t => t.lecturer?.toJSON()),
        new: addTeachingChanges.map(t => t.lecturer?.toJSON()),
      };
  }

  logger.info(changes, '>>>>>>>>>>> CHANGES');

  return changes;
};

export const generateDiff = async (
  oldRefs: ProductUpdateWithReferencesNarrEnt,
  newRefs: ProductUpdateWithReferencesNarrEnt,
) => {
  const allTeachings = [...(oldRefs.teachings ?? []), ...(newRefs.teachings ?? [])];
  const dozentCol = await getCollection(User);
  const dozentList = await dozentCol
    .find({ _id: { $in: allTeachings.map(t => t.lecturerID) } }, { projection: versionDiffTeachingUserSelSet })
    .toArray();

  allTeachings.forEach(t => {
    const attrVal = dozentList.find(d => d._id === t.lecturerID) as Maybe<
      NarrowedEntity<User, typeof versionDiffTeachingUserSelSet>
    >;
    t.lecturer = attrVal;
  });

  return await generateProductVersionDiff(oldRefs, newRefs);
};

export const updateProductsWithReferences = registerMutation({
  operationName: 'updateProductsWithReferences',
  type: () => Int,
  params: [
    { name: 'changes', type: () => ProductUpdateWithReferences },
    { name: 'notifyParticipantsAboutChanges', type: () => Boolean },
  ],
  resolve: (newRefs: ProductUpdateWithReferencesNarrEnt, notifyParticipantsAboutChanges: boolean) => async () => {
    const { product: newProd, teachings: newTeachings, setting: newSettings } = newRefs;
    if (!newProd) throw new Error('parameter "product" is undefined');

    if (newProd._class === 'Workshop') {
      if (!newRefs.appointments && !!(newRefs.product as Workshop).appointments)
        newRefs.appointments = (newProd as Workshop).appointments.map(a => (a as any as { doc: any }).doc) ?? [];
      else (newProd as Workshop).appointments = newRefs.appointments?.map(a => (a as any as { doc: any }).doc) ?? [];
    }

    // === Hole alte Product ===
    const productID = newProd._id;
    const oldProduct = await getByID(Product, productID)(versionDiffWorkshopSelSet);
    if (!oldProduct) throw new Error(`"product._id" = ${productID} not found!`);

    // === Hole alte Teachings ===
    let oldTeachings: VersionDiffTeachings[] | null = null;
    if (newTeachings) {
      oldTeachings = (await getProductTeachings(productID)(versionDiffTeachingSelSet)) as VersionDiffTeachings[] | null;
    }

    // === Hole alte Appointments ===
    let oldAppointments: VersionDiffAppointment[] | null = null;
    if (oldProduct._class === 'Workshop')
      if (newRefs.appointments) {
        oldAppointments = (oldProduct as Workshop).appointments;
      }

    // === Hole alte Settings ===
    let oldSettings: VersionDiffSetting | null = null;
    if (newSettings) oldSettings = await getByID(Setting, newSettings._id)(versionDiffSettingSelSet);

    // Hole von allen geänderten Referenzen die Änderungen und informiere Betroffene per Mail
    const changes = await generateDiff(
      new ProductUpdateWithReferences({
        product: oldProduct,
        ...(oldTeachings ? { teachings: oldTeachings } : {}),
        ...(oldSettings ? { setting: oldSettings } : {}),
        ...(oldAppointments ? { appointments: oldAppointments } : {}),
      }) as ProductUpdateWithReferencesNarrEnt,
      newRefs,
    );

    // === Speichere Product/Workshop & Appointments ===
    if (newProd._id) await newProd.save();
    else await newProd.create();

    // TODO: make that work again. Currently teaching changes are not persisted
    // === Speichere Teachings ===
    // const findTeachingEnity = (teachings: Maybe<VersionDiffTeachings[]> | undefined, lecturer: Teaching) =>
    //   (teachings ?? []).find(t => t.lecturerID === (lecturer as Teaching)._id);
    // const oldAndNewTeachings = [...(newRefs.teachings ?? []), ...(oldTeachings ?? [])];

    // for await (const [isAdd, teachingChanges] of [
    //   ...((changes.teachings?.old ?? []) as VersionDiffTeachings[]).map(t => [false, t]),
    //   ...((changes.teachings?.new ?? []) as VersionDiffTeachings[]).map(t => [true, t]),
    // ]) {
    //   const targetTeaching = findTeachingEnity(oldAndNewTeachings, teachingChanges as Teaching);

    //   if (isAdd) await createTeaching(targetTeaching)();
    //   else await targetTeaching?.remove();
    // }

    // === Speichere Settings ===
    if (newSettings) {
      const tempSetttings = new Setting(newSettings.toJSON());
      if (tempSetttings._id) await tempSetttings.save();
      else await tempSetttings.create();
    }

    if (notifyParticipantsAboutChanges && Object.keys(changes).length > 0) {
      const participations = await loadMongoData(Participation, { productID });
      for (const participation of participations) {
        if (participationRetrievesProductUpdateMail(participation)) {
          await notifyProductChanged(participation.userID, productID, changes);
          await new Promise(res => setTimeout(() => res(null), 50));
        }
      }
    }

    return Object.keys(changes).length;
  },
});
